// src/utils/logError.js
//
// Fire-and-forget client error telemetry. Writes errors to the `clientErrors`
// Firestore collection (create-only for users; readable by admin — see rules).
//
// Usage:  logError('saveRecord', error)
//         logError('exportCSV', error, { view: 'records', rows: 1200 })
//
// Design notes:
// - NEVER throws and never awaits in callers — logging must not break the app.
// - Rate-limited to 10 writes/minute and dedupes identical back-to-back
//   messages, so an error loop can't hammer Firestore (or your bill).
// - Pre-login errors are dropped (rules require auth to create).

import { auth, getFirebaseServices } from '@/firebase';

const MAX_PER_MINUTE = 10;
let recentWrites = [];
let lastSignature = '';
let lastSignatureAt = 0;

export function logError(context, error, extra = {}) {
  // Intentionally not awaited by callers; swallow everything internally.
  writeError(context, error, extra).catch((e) => {
    // Last resort: telemetry itself failed. Console only — never rethrow.
    console.error('[logError] failed to record error:', e);
  });
}

async function writeError(context, error, extra) {
  if (!auth.currentUser) return; // rules require auth; skip pre-login errors

  const message = String(error?.message || error || 'unknown error').slice(0, 1000);

  // Dedupe identical errors fired within 5 seconds (e.g. render loops)
  const signature = `${context}|${message}`;
  const now = Date.now();
  if (signature === lastSignature && now - lastSignatureAt < 5000) return;
  lastSignature = signature;
  lastSignatureAt = now;

  // Rate limit: max 10 writes per rolling minute
  recentWrites = recentWrites.filter((t) => now - t < 60000);
  if (recentWrites.length >= MAX_PER_MINUTE) return;
  recentWrites.push(now);

  let safeExtra = {};
  try {
    safeExtra = JSON.parse(JSON.stringify(extra ?? {}));
  } catch {
    safeExtra = { note: 'extra was not serializable' };
  }

  const { db } = await getFirebaseServices();
  const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');

  await addDoc(collection(db, 'clientErrors'), {
    context: String(context || 'unknown').slice(0, 200),
    message,
    stack: String(error?.stack || '').slice(0, 4000),
    code: error?.code || null, // Firebase errors carry useful codes
    extra: safeExtra,
    userId: auth.currentUser.uid,
    url: typeof window !== 'undefined' ? window.location.href.slice(0, 500) : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    appVersion: import.meta?.env?.VITE_APP_VERSION || process?.env?.VUE_APP_VERSION || null,
    timestamp: serverTimestamp(),
  });
}